import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

const AnnouncementContext = createContext(null);

export const useAnnouncements = () => {
  const context = useContext(AnnouncementContext);
  if (!context) {
    // Return safe default if context not available
    return { unreadCount: 0, decrementUnread: () => {}, refreshUnreadCount: () => {} };
  }
  return context;
};

export const AnnouncementProvider = ({ children }) => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUnreadCount = useCallback(async () => {
    if (!user) {
      setUnreadCount(0);
      setIsLoading(false);
      return;
    }
    
    try {
      const response = await api.get('/announcements/unread-count');
      setUnreadCount(response.data.unread_count);
    } catch (err) {
      console.error('Failed to fetch unread count:', err);
      setUnreadCount(0);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Fetch on mount and when user changes
  useEffect(() => {
    if (user) {
      fetchUnreadCount();
    } else {
      setUnreadCount(0);
    }
  }, [user, fetchUnreadCount]);

  // Refresh every 60 seconds when authenticated
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(fetchUnreadCount, 60000);
    return () => clearInterval(interval);
  }, [user, fetchUnreadCount]);

  const decrementUnread = () => {
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const refreshUnreadCount = () => {
    fetchUnreadCount();
  };

  return (
    <AnnouncementContext.Provider value={{ unreadCount, decrementUnread, refreshUnreadCount }}>
      {children}
    </AnnouncementContext.Provider>
  );
};
